/* eslint-disable @typescript-eslint/member-ordering */

import { logger } from '@accedo/logger';
import { IDPService, LoginRequest, RegisterRequest } from '##/services/elevate/types';
import { User } from '##/dataModels/user';
import { getNZOSBaseUrl } from '##/services/nzos/config';

const LP = '[NzosIDPService]';

const PUBLIC_PATH = process.env.PUBLIC_PATH || '/';
export const AUTH_USER_KEY = `I_U_K_${PUBLIC_PATH}`; // IDP User Key

/**
 * Device authorization response from /auth/device/authorization.
 */
interface DeviceAuthorizationResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete: string;
  expires_in: number;
  interval: number;
}

/**
 * Device token error response from /auth/device/token.
 */
interface DeviceTokenError {
  error: 'authorization_pending' | 'slow_down' | 'access_denied' | 'expired_token' | 'invalid_request';
  error_description?: string;
}

/**
 * Auth API response shared by login, refresh, and device token endpoints.
 */
interface AuthApiResponse {
  id: string;
  email: string;
  accessToken: { token: string; expiresAt: number };
  refreshToken?: { token: string; expiresAt: number };
}

/** NZOS IDP Service implementing device authorization flow and standard authentication. */
class NzosIDPService implements IDPService {
  // eslint-disable-next-line no-use-before-define
  private static instance: NzosIDPService;
  private user: User | null = null;
  private timeoutId: NodeJS.Timeout | undefined;
  private deviceAuthorizationData: DeviceAuthorizationResponse | null = null;
  private pollingTimeoutId: NodeJS.Timeout | undefined;
  private pollingActive = false;
  // Deduplicates concurrent refresh() calls — returns the in-flight promise if one exists.
  private refreshPromise: Promise<User> | null = null;
  // Incremented on logout; in-flight refresh checks this to avoid saving a stale result.
  private refreshGeneration = 0;

  private constructor() {
    logger.debug(`${LP} Creating new instance of NzosIDPService`);
  }

  static getInstance(): NzosIDPService {
    if (!NzosIDPService.instance) {
      NzosIDPService.instance = new NzosIDPService();
    }
    return NzosIDPService.instance;
  }

  private static async apiCall<T>({
    endpoint,
    method = 'GET',
    body,
    token,
    contentType = 'application/json',
  }: {
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    token?: string;
    contentType?: string;
  }): Promise<T> {
    const baseUrl = getNZOSBaseUrl();
    const url = `${baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': contentType,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const options: RequestInit = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      options.body = contentType === 'application/json' ? JSON.stringify(body) : (body as string);
    }

    logger.debug(`${LP} API call: ${method} ${url}`);

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`${LP} API error: ${response.status} ${errorText}`);
      throw new Error(`API Error: ${response.status} ${errorText}`);
    }

    // Handle 204 No Content responses (logout)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  private scheduleTokenRefresh(user: User): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    const refreshTime = user.authToken.expiration - Date.now() - 60000;

    if (refreshTime > 0) {
      this.timeoutId = setTimeout(() => {
        this.refresh({
          token: user.refreshToken.token,
        })
          .then((refreshedUser) => {
            logger.debug(`${LP} Background token refresh succeeded, notifying app`);
            globalThis.dispatchEvent(new CustomEvent('nzos:auth:token-refreshed', { detail: refreshedUser }));
          })
          .catch((error) => {
            logger.error(`${LP} Auto-refresh failed: ${error}`);
            this.clearUser();
            // Notify the app so it can log the user out of the UI
            globalThis.dispatchEvent(new CustomEvent('nzos:auth:refresh-failed'));
          });
      }, refreshTime);
    } else {
      logger.warn(`${LP} Token already expired or within buffer, refreshing immediately`);
      this.refresh({
        token: user.refreshToken.token,
      })
        .then((refreshedUser) => {
          logger.debug(`${LP} Immediate token refresh succeeded, notifying app`);
          globalThis.dispatchEvent(new CustomEvent('nzos:auth:token-refreshed', { detail: refreshedUser }));
        })
        .catch((error) => {
          logger.error(`${LP} Immediate refresh failed: ${error}`);
          this.clearUser();
          globalThis.dispatchEvent(new CustomEvent('nzos:auth:refresh-failed'));
        });
    }
  }

  private clearUser(): void {
    localStorage.removeItem(AUTH_USER_KEY);
    this.user = null;
    this.refreshGeneration += 1;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = undefined;
    this.stopPolling();
    this.deviceAuthorizationData = null;
  }

  private stopPolling(): void {
    this.pollingActive = false;
    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
      this.pollingTimeoutId = undefined;
    }
  }

  /** Maps auth API response to User model. Token expiry is converted from seconds to ms. */
  private static mapApiResponseToUser(data: AuthApiResponse): User {
    return {
      id: data.id,
      email: data.email,
      name: data.email.split('@')[0],
      authToken: {
        token: data.accessToken.token,
        expiration: data.accessToken.expiresAt * 1000,
      },
      refreshToken: data.refreshToken
        ? {
            token: data.refreshToken.token,
            expiration: data.refreshToken.expiresAt * 1000,
          }
        : {
            token: '',
            expiration: 0,
          },
    };
  }

  private saveUser(user: User): void {
    this.user = user;
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    this.scheduleTokenRefresh(user);
  }

  /** Initiate device authorization flow (Step 1) — returns QR code URL and user code. */
  async initiateDeviceAuthorization(): Promise<DeviceAuthorizationResponse> {
    logger.debug(`${LP} Initiating device authorization`);

    const body = new URLSearchParams({
      client_id: 'nzos_tv_app',
      scope: 'read write',
    });

    const response = await NzosIDPService.apiCall<DeviceAuthorizationResponse>({
      endpoint: '/auth/device/authorization',
      method: 'POST',
      body: body.toString(),
      contentType: 'application/x-www-form-urlencoded',
    });

    this.deviceAuthorizationData = response;

    logger.debug(`${LP} Device authorization initiated. User code: ${response.user_code}`);

    return response;
  }

  /** Poll for device authorization completion (Step 2). */
  async pollDeviceToken({
    deviceCode,
    onSuccess,
    onError,
    onPending,
  }: {
    deviceCode: string;
    onSuccess: (user: User) => void;
    onError: (error: DeviceTokenError) => void;
    onPending?: () => void;
  }): Promise<void> {
    if (this.pollingActive) {
      logger.warn(`${LP} Polling already in progress, ignoring new poll request`);
      return;
    }

    // Use the interval provided by the authorization server (RFC 8628 §3.5).
    // Fall back to 5s if the value is missing or invalid.
    const interval = this.deviceAuthorizationData?.interval ?? 5;
    let pollInterval = interval * 1000; // Convert to milliseconds

    const poll = async (): Promise<void> => {
      const continuePolling = (): void => {
        if (this.pollingActive) {
          this.pollingTimeoutId = setTimeout(poll, pollInterval);
        }
      };

      try {
        const body = new URLSearchParams({
          grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
          device_code: deviceCode,
          client_id: 'nzos_tv_app',
        });

        const url = `${getNZOSBaseUrl()}/auth/device/token`;

        logger.debug(`${LP} Polling device token`);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: body.toString(),
        });

        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          logger.error(`${LP} Failed to parse response as JSON (status ${response.status}):`, parseError);
          throw new Error(`Invalid JSON response: ${response.statusText}`);
        }

        logger.debug(`${LP} Poll response received (status ${response.status}):`, data);

        if (!response.ok || 'error_code' in data || 'error' in data) {
          const errorCode = data.error_code || data.error;

          if (errorCode === 'authorization_pending') {
            logger.debug(`${LP} Authorization pending, next poll in ${pollInterval}ms`);
            onPending?.();
            continuePolling();
            return;
          }

          if (errorCode === 'slow_down') {
            pollInterval += 5000; // RFC 8628 §3.5: increase by 5 seconds
            logger.debug(`${LP} Polling too fast (slow_down), increasing interval to ${pollInterval}ms`);
            continuePolling();
            return;
          }

          logger.error(
            `${LP} Device authorization failed with status ${response.status}: ${errorCode || 'unknown error'}`,
          );
          this.stopPolling();
          onError({
            error: (errorCode || 'server_error') as DeviceTokenError['error'],
            error_description: data.message || data.error_description || `HTTP ${response.status}`,
          });
          return;
        }

        if (!data.email || !data.accessToken) {
          logger.error(`${LP} Invalid response data - missing required fields:`, data);
          this.stopPolling();
          onError({
            error: 'invalid_request',
            error_description: 'Server returned invalid user data',
          });
          return;
        }

        const user = NzosIDPService.mapApiResponseToUser(data as AuthApiResponse);
        logger.debug(`${LP} Device authorization successful for user: ${user.email}`);

        this.stopPolling();
        this.deviceAuthorizationData = null;

        this.saveUser(user);
        onSuccess(user);
      } catch (error) {
        // RFC 8628 §3.5: network errors are not terminal — keep polling
        logger.warn(`${LP} Transient error during device token poll, will retry: ${error}`);
        continuePolling();
      }
    };

    this.pollingActive = true;
    logger.debug(`${LP} Starting device token polling with interval: ${pollInterval / 1000}s`);
    this.pollingTimeoutId = setTimeout(poll, pollInterval);
  }

  stopDeviceAuthorizationPolling(): void {
    this.stopPolling();
    this.deviceAuthorizationData = null;
  }

  /**
   * Standard email/password login.
   * @param loginRequest - Login credentials (email, password).
   * @returns User object with tokens.
   */
  async login(loginRequest: LoginRequest): Promise<User> {
    logger.debug(`${LP} login called with email: ${loginRequest.email}`);

    this.clearUser();

    const body = {
      email: loginRequest.email,
      password: loginRequest.password,
    };

    const response = await NzosIDPService.apiCall<AuthApiResponse>({
      endpoint: '/auth/login',
      method: 'POST',
      body,
    });

    const user = NzosIDPService.mapApiResponseToUser(response);
    this.saveUser(user);

    return user;
  }

  /**
   * Logout user and invalidate tokens.
   * @param args - Logout configuration.
   * @param args.token - Optional auth token to invalidate (uses current user's token if not provided).
   * @param args.segment - Unused, kept for interface compatibility.
   */
  async logout({ token }: { token?: string; segment?: string }): Promise<void> {
    logger.debug(`${LP} logout called`);

    const authToken = token || this.user?.authToken.token;
    const refreshToken = this.user?.refreshToken.token;

    if (authToken && refreshToken) {
      try {
        await NzosIDPService.apiCall<void>({
          endpoint: '/auth/logout',
          method: 'POST',
          token: authToken,
          body: { token: refreshToken },
        });
      } catch (error) {
        logger.error(`${LP} Error during logout API call: ${error}`);
      }
    }

    this.clearUser();
  }
  /**
   * Refresh access token using refresh token.
   * @param args - Refresh configuration.
   * @param args.token - Refresh token.
   * @param args.segment - Unused, kept for interface compatibility.
   * @returns Updated user object with new tokens.
   */

  async refresh({ token }: { token: string; segment?: string }): Promise<User> {
    logger.debug(`${LP} refresh called`);

    if (this.refreshPromise) {
      logger.debug(`${LP} refresh already in-flight, reusing existing promise`);
      return this.refreshPromise;
    }

    const generation = this.refreshGeneration;

    this.refreshPromise = NzosIDPService.apiCall<AuthApiResponse>({
      endpoint: '/auth/refresh',
      method: 'POST',
      body: { token },
    })
      .then((response) => {
        const user = NzosIDPService.mapApiResponseToUser(response);
        if (generation !== this.refreshGeneration) {
          logger.warn(`${LP} Refresh completed after logout, discarding result`);
          return user;
        }
        this.saveUser(user);
        return user;
      })
      .catch((error) => {
        logger.error(`${LP} Error refreshing token: ${error}`);
        if (generation === this.refreshGeneration) {
          this.clearUser();
        }
        throw error;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  // NOTE: NZOS API does not support registration; users must register at nzonscreen.com.
  // eslint-disable-next-line class-methods-use-this
  async register(_registerRequest: RegisterRequest, _segment?: string): Promise<User> {
    logger.error(`${LP} register called - NOT SUPPORTED by NZOS API`);
    throw new Error('Registration is not supported. Please register at https://www.nzonscreen.com');
  }

  /**
   * Get current user from memory or localStorage.
   * @returns Current user or null if not authenticated.
   */
  getUser(): User | null {
    if (this.user) {
      return this.user;
    }

    const userStr = localStorage.getItem(AUTH_USER_KEY);
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
        if (this.user) {
          this.scheduleTokenRefresh(this.user);
        }
        return this.user;
      } catch (error) {
        logger.error(`${LP} Error parsing stored user: ${error}`);
        localStorage.removeItem(AUTH_USER_KEY);
        return null;
      }
    }

    return null;
  }

  /**
   * Get current user details from the server.
   * Fetches fresh user data from /auth/user endpoint.
   * @param args - Configuration object.
   * @param args.token - Authentication token.
   * @returns Fresh user data from server.
   */
  async getUserDetails({ token }: { token: string }): Promise<User> {
    logger.debug(`${LP} getUserDetails called`);

    const user = await NzosIDPService.apiCall<User>({
      endpoint: '/auth/user',
      method: 'GET',
      token,
    });

    this.saveUser(user);

    return user;
  }

  /**
   * Get current device authorization data.
   * Used to retrieve QR code URL and user code for display.
   * @returns Device authorization data or null.
   */
  getDeviceAuthorizationData(): DeviceAuthorizationResponse | null {
    return this.deviceAuthorizationData;
  }
}

export { NzosIDPService };
